import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { check, nullable, object, pipe, string } from "valibot";
import {
  currency,
  dataName,
  decimalMark,
  decimalValue,
  errorTranslation,
  integerValue,
  label,
  rememberInput,
  required,
} from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { NumberInput } from "../../components/inputs/NumberInput";
import { isNil } from "lodash-es";
import { MAX_DECIMAL_PRECISION, MAX_NUMBER_XL, MAX_NUMBER_XL_NEGATIVE, NUMBER_CONDITION_KEYS } from "../../constants";
import { createOptionsForCurrencies } from "../../utils/selectOptionsUtil";
import { createMemo } from "solid-js";
import { defaultNumberFilter } from "../../pages/customer/form/filter";
import { SingleSelectInput } from "../../components/menu/Select";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { toIsoCurrencySymbol } from "../../utils/currencyUtil";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage";
import { keyFromSelector } from "i18next";

const Schema = pipe(
  object({
    label_text: label(),
    data_name: dataName(),
    currency: currency(),
    decimal_mark: decimalMark(),
    precision: nullable(integerValue(0, MAX_DECIMAL_PRECISION)),
    min: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    max: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    required: required(),
    remember_input: rememberInput(),
  }),
  check(
    ({ min, max }) => {
      const maxValue = !isNil(max) ? Math.min(max, MAX_NUMBER_XL) : Infinity;
      const minValue = !isNil(min) ? Math.max(min, MAX_NUMBER_XL_NEGATIVE) : -Infinity;
      return maxValue >= minValue;
    },
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMERIC_MAX_GREATER_EQUALS_MIN)),
  ),
);

const ValueSchema = string();

export type PriceFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const PriceField: Field<PriceFieldProperties, DefaultDtoType, ValueType> = {
  type: "price",
  category: "number",
  widget: "com.dirkjanhoek:price:4",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    currency: "EUR",
    decimal_mark: "comma",
    min: null,
    max: null,
    precision: 2,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: ["calculation", "lookup", "number", "price", "radio", "search", "slider"],
        variableTypes: ["number"],
      },
    },
  },
  filter: {
    type: "number",
    definition: defaultNumberFilter,
  },
  stringValue: (value) => value,
  renderValue: (value) => value,
  icon: "currencyEuro",
  titleKey: keyFromSelector(($) => $.PRICE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.PRICE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    currency: keyFromSelector(($) => $.FIELD_PROPERTY_CURRENCY),
    decimal_mark: keyFromSelector(($) => $.FIELD_PROPERTY_DECIMAL_MARK),
    precision: keyFromSelector(($) => $.FIELD_PROPERTY_PRECISION),
    min: keyFromSelector(($) => $.FIELD_PROPERTY_NUMERIC_MIN),
    max: keyFromSelector(($) => $.FIELD_PROPERTY_NUMERIC_MAX),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <InputPreview leftSymbol={toIsoCurrencySymbol(props.properties.currency)} />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();
    const currencyOptions = createMemo(() => createOptionsForCurrencies(t));
    const globalErrors = createMemo(() => props.getGlobalErrors());

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_FORMAT)}>
          <SingleSelectInput
            label={t(props.propertyKeys.currency)}
            value={props.data.properties.currency}
            errorMessage={props.getError("currency")}
            options={currencyOptions()}
            onSelect={(value) => props.setValue("currency", value)}
            info={{
              title: t(props.propertyKeys.currency),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.currency }),
              description: t(($) => $.FIELD_PROPERTY_CURRENCY_DESCRIPTION),
            }}
            sameWidth
          />
          <SingleSelectInput
            label={t(props.propertyKeys.decimal_mark)}
            value={props.data.properties.decimal_mark}
            errorMessage={props.getError("decimal_mark")}
            options={[
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_DECIMAL_MARK_COMMA), value: "comma" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_DECIMAL_MARK_PERIOD), value: "period" },
            ]}
            onSelect={(value) => props.setValue("decimal_mark", value)}
            info={{
              title: t(props.propertyKeys.decimal_mark),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.decimal_mark }),
              description: t(($) => $.FIELD_PROPERTY_DECIMAL_MARK_DESCRIPTION),
            }}
            sameWidth
          />
          <NumberInput
            label={t(props.propertyKeys.precision)}
            value={props.data.properties.precision}
            minValue={0}
            maxValue={MAX_DECIMAL_PRECISION}
            onBlur={(value) => props.setValue("precision", value)}
            errorMessage={props.getError("precision")}
            info={{
              title: t(props.propertyKeys.precision),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.precision }),
              description: t(($) => $.FIELD_PROPERTY_PRECISION_DESCRIPTION),
            }}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
          <NumberInput
            label={t(props.propertyKeys.min)}
            value={props.data.properties.min}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("min", value)}
            errorMessage={props.getError("min")}
          />
          <NumberInput
            label={t(props.propertyKeys.max)}
            value={props.data.properties.max}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("max", value)}
            errorMessage={props.getError("max")}
          />
          <GlobalErrorInlineMessage errors={globalErrors()} />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(PriceField);
